import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { mateApi } from "@/api/mateApi";
import { MateCard, InviteInput } from "@/types/mateTypes";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useAuthStore } from "@/store/auth";
import { MATE_CONFIG } from "@/config/mateConfig";

// React Query 查询键定义
// Query keys for React Query caching
const QUERY_KEYS = {
  mates: ["mates", "list"] as const,
  matesWithPending: ["mates", "with-pending"] as const,
  me: ["me", "profile"] as const,
  invites: ["mates", "invites"] as const,
} as const;

// 获取所有伙伴列表
// Get all mates
export function useMates() {
  const { token, logout } = useAuthStore();
  const navigate = useNavigate();
  const { handleError } = useToastHandler();

  return useQuery({
    enabled: !!token,
    queryKey: QUERY_KEYS.mates,
    staleTime: MATE_CONFIG.QUERY_STALE_TIME,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async () => {
      try {
        // 调用 API 获取伙伴列表
        // Call API to get mates list
        const mates = await mateApi.getMates();
        return mates;
      } catch (err: unknown) {
        let http_status = 0;
        if (err instanceof AxiosError && err.response?.status) {
          http_status = err.response.status;
        }
        // 处理认证失败，自动登出并跳转到登录页
        // Handle authentication failure, auto logout and redirect to login
        if (http_status === 401 || http_status === 403) {
          handleError("Authentication failed. Please login again.", 3000);
          logout();
          navigate("/login");
          throw err;
        }

        // 处理其他错误并显示提示
        // Handle other errors and show notification
        handleError(
          err instanceof Error ? err.message : "Failed to fetch mates",
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );
        throw err;
      }
    },
  });
}

// 获取伙伴列表和待处理邀请
// Get mates with pending invites
export function useMatesWithPending() {
  const { token, logout } = useAuthStore();
  const navigate = useNavigate();
  const { handleError } = useToastHandler();

  return useQuery({
    enabled: !!token,
    queryKey: QUERY_KEYS.matesWithPending,
    staleTime: MATE_CONFIG.QUERY_STALE_TIME,
    refetchOnWindowFocus: true,
    retry: 1,
    queryFn: async () => {
      try {
        const data = await mateApi.getMatesWithPending();
        return data;
      } catch (err: unknown) {
        let http_status = 0;
        if (err instanceof AxiosError && err.response?.status) {
          http_status = err.response.status;
        }
        if (http_status === 401 || http_status === 403) {
          handleError("Authentication failed. Please login again.", 3000);
          logout();
          navigate("/login");
          throw err;
        }

        handleError(
          err instanceof Error ? err.message : "Failed to fetch mates",
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );
        throw err;
      }
    },
  });
}

// 邀请新伙伴
// Invite a mate
export function useInviteMate() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async (input: InviteInput) => {
      return await mateApi.inviteMate(input);
    },
    onSuccess: () => {
      // 成功后刷新伙伴列表缓存
      // Invalidate both mates queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.matesWithPending });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      handleError(error, 3000);
    },
  });
}

// 接受邀请
// Accept an invite
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async (token: string) => {
      return await mateApi.acceptInvite(token);
    },
    onSuccess: () => {
      // 成功处理由组件负责，这里刷新伙伴列表
      // Success handled by the component, refresh mates list here
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.matesWithPending });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      handleError(error, 3000);
    },
  });
}

// 拒绝邀请
// Reject an invite
export function useRejectInvite() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async (token: string) => {
      return await mateApi.rejectInvite(token);
    },
    onSuccess: () => {
      // Success handled by the component
      // Invalidate mates list to refresh
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.matesWithPending });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      handleError(error, 3000);
    },
  });
}

// 标记所有伙伴为已读
// Mark all mates as read
export function useMarkAllRead() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async () => {
      return await mateApi.markAllRead();
    },
    onMutate: async () => {
      // 取消正在进行的查询请求
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.mates });

      // 保存当前数据快照
      // Snapshot the previous value
      const previousMates = queryClient.getQueryData<MateCard[]>(
        QUERY_KEYS.mates
      );

      // 乐观更新：立即将所有伙伴标记为已读
      // Optimistically update to the new value
      if (previousMates) {
        const now = new Date().toISOString();
        queryClient.setQueryData<MateCard[]>(
          QUERY_KEYS.mates,
          (old) =>
            old?.map((mate) => ({
              ...mate,
              has_unread: false,
              my_last_seen_at: now,
            })) ?? []
        );
      }

      // 返回上下文对象，包含快照数据
      // Return a context object with the snapshotted value
      return { previousMates };
    },
    onError: (_, __, context) => {
      // 如果变更失败，回滚到之前的数据状态
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMates) {
        queryClient.setQueryData(QUERY_KEYS.mates, context.previousMates);
      }
      handleError("Failed to mark all as read", 3000);
    },
    onSettled: () => {
      // 无论成功还是失败都重新获取数据
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
    },
  });
}

// 移除伙伴关系
// Remove a mate
export function useRemoveMate() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async (mateshipId: string) => {
      return await mateApi.revokeMate(mateshipId);
    },
    onSuccess: (_, mateshipId) => {
      // 乐观更新：立即从列表中移除该伙伴
      // Remove the mate from the list optimistically
      queryClient.setQueryData<MateCard[]>(
        QUERY_KEYS.mates,
        (old) => old?.filter((mate) => mate.mateship_id !== mateshipId) ?? []
      );
      // 成功消息由组件处理
      // Success message handled by component
    },
    onError: (error: AxiosError<{ message: string }>) => {
      handleError(error, 3000);
      // 重新获取数据以确保一致性
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
    },
  });
}

// 取消待处理的邀请
// Cancel a pending invite
export function useCancelInvite() {
  const queryClient = useQueryClient();
  const { handleError } = useToastHandler();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      return await mateApi.cancelInvite(inviteId);
    },
    onSuccess: () => {
      // Invalidate both mates queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.matesWithPending });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      handleError(error, 3000);
    },
  });
}

// 心跳检测（活跃状态跟踪）
// Heartbeat (activity tracking)
export function useHeartbeatMutation() {
  return useMutation({
    mutationFn: async () => {
      return await mateApi.sendHeartbeat();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      // 心跳失败时静默处理 - 不向用户显示错误
      // Silently fail for heartbeat - don't show error to user
      console.warn(
        "Heartbeat failed:",
        error.response?.data?.message || error.message
      );
    },
  });
}

// 工具 Hook：检查是否有未读伙伴消息
// Utility hook to check if there are any unread mates
export function useUnreadBadge() {
  const { data: mates } = useMates();
  return {
    hasUnread: mates?.some((mate) => mate.has_unread) ?? false,
    unreadCount: mates?.filter((mate) => mate.has_unread).length ?? 0,
  };
}

// 缓存失效辅助函数
// Invalidation helpers
export function useInvalidateMates() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mates });
  };
}
